import { AlertTriangle, BellOff, Inbox } from "lucide-react";
import { memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useNotifications } from "@/api/notifications/useNotifications";
import { SpinningLoader } from "@/components/SpinningLoader";

import { NotificationCard } from "./NotificationCard";

interface INotificationsListProps {
  isOpen: boolean;
  onlyUnread: boolean;
  onCloseDrawer: () => void;
}

const NotificationsListComponent = ({
  isOpen,
  onlyUnread,
  onCloseDrawer,
}: INotificationsListProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useNotifications({ onlyUnread, enabled: isOpen });

  // NOTE: IntersectionObserver-based infinite scroll. Sentinel sits at the
  // bottom of the list; when it enters the viewport we request the next page.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          fetchNextPage();
        }
      },
      { rootMargin: "100px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  if (isLoading) {
    return (
      // NOTE: Radix `ScrollArea`'s Viewport renders its child inside an
      // internal `display: table; min-width:100%` wrapper that only sizes
      // to content — so plain `h-full` collapses and the spinner clings to
      // the top. Pin the height to the visible drawer body via
      // `min-h-[calc(100dvh-7rem)]` (drawer is full-height; the SheetHeader
      // is roughly 126px tall). `dvh` instead of `vh` so the calc accounts
      // for mobile browser chrome correctly.
      <div
        data-testid="notifications-loading"
        className="flex min-h-[calc(100dvh-126px)] items-center justify-center"
      >
        <SpinningLoader />
      </div>
    );
  }

  // NOTE: Match the empty-state layout (icon disc + title + subtitle) so the
  // drawer feels consistent across all zero-content states.
  if (isError) {
    return (
      <div
        data-testid="notifications-error"
        className="px-6 py-20 flex flex-col items-center text-center"
      >
        <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <p className="text-sm font-medium text-foreground">
          {t("load_error_title")}
        </p>
        {/* NOTE: `load_error_web` (not `load_error`) — the mobile drawer has
            a real RefreshControl, web doesn't. The mobile copy says "pull
            down to refresh" which doesn't apply on web; this key tells the
            user to close + reopen the panel instead. */}
        <p className="mt-1 text-xs text-muted-foreground max-w-xs">
          {t("load_error_web")}
        </p>
      </div>
    );
  }

  const notifications = data?.pages.flatMap((page) => page.notifications) ?? [];

  if (notifications.length === 0) {
    const EmptyIcon = onlyUnread ? BellOff : Inbox;
    return (
      <div
        data-testid={
          onlyUnread ? "notifications-empty-unread" : "notifications-empty"
        }
        className="px-6 py-20 flex flex-col items-center text-center"
      >
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <EmptyIcon className="h-8 w-8 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-foreground">
          {onlyUnread ? t("empty_unread_title") : t("empty_title")}
        </p>
        <p className="mt-1 text-xs text-muted-foreground max-w-xs">
          {onlyUnread ? t("empty_unread") : t("empty")}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="notifications-list"
      className="flex flex-col gap-2 px-4 py-3"
    >
      {notifications.map((notification) => (
        <NotificationCard
          key={notification.uuid}
          notification={notification}
          onCloseDrawer={onCloseDrawer}
        />
      ))}
      <div ref={sentinelRef} className="h-4" aria-hidden="true" />
      {isFetchingNextPage && (
        // NOTE: Three-dot typing indicator for the lazy-load tail. Matches
        // the streaming/help-chat patterns elsewhere in the app — staggered
        // `animation-delay` produces the "thinking" bounce sequence.
        <div
          data-testid="notifications-fetching-next-page"
          className="py-3 flex items-center justify-center gap-1"
        >
          <span className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.3s]" />
          <span className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.15s]" />
          <span className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce" />
        </div>
      )}
    </div>
  );
};

export const NotificationsList = memo(NotificationsListComponent);
